import { DiagnosticReport, Procedure } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import _ from "lodash";
import {
  getReferenceId,
  getValidCode,
  isReferenceDiagnosticReport,
} from "../../../shared-components/fhir-sections/shared";
import { getProcedureType } from "./procedure-type";

type ProcedureOccurrence = {
  rawProcedure: Smart<Procedure>;
  date: string | undefined;
  time: string | undefined;
  status: string;
};

type GroupedProcedures = {
  title: string;
  mostRecentProcedure: Smart<Procedure>;
  firstProcedure: Smart<Procedure>;
  sortedOccurrences?: ProcedureOccurrence[];
  status: string;
};

export type ProcedureRowData = {
  id: string;
  procedure: string;
  datePerformed: string;
  status: string;
  originalData: Smart<Procedure>;
  diagnosticReports: DiagnosticReport[];
  type: string;
  bodyRegion: string;
};

export function getProcedureRows({ bundle }: { bundle: FhirBundleSdk }): ProcedureRowData[] {
  const procedures = bundle.getProcedures();

  // Use local grouping logic similar to conditions
  const groupedProcedures = groupProceduresV2(procedures);

  // Convert to row data format
  return groupedProcedures.map(procedure => {
    const mostRecentProcedure = procedure.mostRecentProcedure;
    const firstProcedure = procedure.firstProcedure;
    const diagnosticReports = getProcedureDiagnosticReports(mostRecentProcedure, bundle);
    const type = getProcedureType(mostRecentProcedure);
    const bodyRegion = getBodyRegionDisplay(mostRecentProcedure);

    return {
      id: mostRecentProcedure.id ?? "-",
      procedure: procedure.title,
      datePerformed: formatDate(
        mostRecentProcedure.performedDateTime || mostRecentProcedure.performedPeriod?.start
      ),
      status: procedure.status || "-",
      originalData: firstProcedure,
      diagnosticReports,
      type,
      bodyRegion,
    };
  });
}

function getBodyRegionDisplay(procedure: Procedure): string {
  const bodySite = procedure.bodySite?.[0];
  if (!bodySite) return "-";

  // Prefer text field if available
  if (bodySite.text) {
    return cleanBodyRegionText(bodySite.text);
  }

  // Fall back to coding display
  const display = bodySite.coding?.[0]?.display;
  if (display) {
    return cleanBodyRegionText(display);
  }

  return "-";
}

function cleanBodyRegionText(text: string): string {
  // Remove common SNOMED suffixes to make display more concise
  return text
    .replace(/\s*\(body structure\)\s*$/i, "")
    .replace(/\s*\(specimen\)\s*$/i, "")
    .replace(/\s+structure$/i, "")
    .trim();
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}

function groupProceduresV2(procedures: Smart<Procedure>[]): GroupedProcedures[] {
  const results: GroupedProcedures[] = [];
  const procedureMap: { [k: string]: ProcedureOccurrence[] } = {};

  procedures.map(p => {
    let title: string;
    const codings = getValidCode(p.code?.coding);
    const displays = codings.map(coding => coding.display);
    const text = p.code?.text;

    if (displays.length) {
      title = Array.from(new Set(displays)).join(", ");
    } else if (text) {
      title = text;
    } else {
      results.push({
        title: "-",
        mostRecentProcedure: p,
        firstProcedure: p,
        status: getProcedureStatus(p),
      });
      return;
    }

    if (!p.performedPeriod && !p.performedDateTime) {
      results.push({
        title,
        mostRecentProcedure: p,
        firstProcedure: p,
        status: getProcedureStatus(p),
      });
      return;
    }

    const dateTime = getPerformedTime(p);

    const procedurePoint = {
      rawProcedure: p,
      date: dateTime?.date,
      time: dateTime?.time,
      status: getProcedureStatus(p),
    };

    const groupedProcedure = procedureMap[title];
    if (groupedProcedure) {
      groupedProcedure.push(procedurePoint);
    } else {
      procedureMap[title] = [procedurePoint];
    }
  });

  Object.entries(procedureMap).map(([title, values]) => {
    const sortedOccurrences = values.sort((a, b) => {
      const aDateTime = combineDateTime(a.date, a.time);
      const bDateTime = combineDateTime(b.date, b.time);

      const dateA = aDateTime ? new Date(aDateTime).getTime() : 0;
      const dateB = bDateTime ? new Date(bDateTime).getTime() : 0;

      return dateB - dateA;
    });

    const mostRecent = sortedOccurrences[0];
    if (!mostRecent) return;
    const firstProcedure = sortedOccurrences[sortedOccurrences.length - 1];
    if (!firstProcedure) return;

    results.push({
      title,
      mostRecentProcedure: mostRecent.rawProcedure,
      firstProcedure: firstProcedure.rawProcedure,
      sortedOccurrences,
      status: mostRecent.status,
    });
  });

  return results;
}

function getProcedureStatus(procedure: Smart<Procedure>): string {
  return procedure.status ?? "-";
}

function getPerformedTime(procedure: Smart<Procedure>):
  | {
      date: string | undefined;
      time: string | undefined;
    }
  | undefined {
  const performedDateTime = procedure.performedDateTime;
  const performedPeriodStart = procedure.performedPeriod?.start;
  const performedPeriodEnd = procedure.performedPeriod?.end;

  const time = performedDateTime || performedPeriodStart || performedPeriodEnd;
  if (!time) return undefined;
  const dateTimeString = dayjs.utc(time).format("YYYY-MM-DD HH:mm:ss");
  return {
    date: dateTimeString.split(" ")[0] ?? undefined,
    time: dateTimeString.split(" ").slice(1).join(" "),
  };
}

function combineDateTime(date: string | undefined, time: string | undefined): string | undefined {
  if (date && time) {
    return `${date} ${time}`;
  } else if (date) {
    return date;
  }
  return undefined;
}

function getProcedureDiagnosticReports(
  procedure: Smart<Procedure>,
  bundle: FhirBundleSdk
): DiagnosticReport[] {
  if (!procedure.report) return [];
  return _(procedure.report)
    .filter(isReferenceDiagnosticReport)
    .map(getReferenceId)
    .map(id => bundle.getResourceById(id))
    .compact()
    .value() as DiagnosticReport[];
}
